import { useCallback, useEffect, useState } from "react";
import { api } from "@/services/api";
import type { MyPortfolio, MyPortfolioUpdatePayload, PortfolioSkillOptions } from "@/types/api";

interface UsePortfolioReturn {
  portfolio: MyPortfolio | null;
  skillOptions: PortfolioSkillOptions | null;
  isLoading: boolean;
  error: string | null;
  save: (payload: MyPortfolioUpdatePayload) => Promise<MyPortfolio>;
  uploadPhoto: (file: File) => Promise<MyPortfolio>;
  uploadCv: (file: File) => Promise<MyPortfolio>;
  publish: (consent: boolean) => Promise<MyPortfolio>;
  unpublish: () => Promise<MyPortfolio>;
}

/** Fetch and manage the current user's portfolio for a given programme (KC-417). */
export function usePortfolio(programmeId: string | null): UsePortfolioReturn {
  const [portfolio, setPortfolio] = useState<MyPortfolio | null>(null);
  const [skillOptions, setSkillOptions] = useState<PortfolioSkillOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPortfolio = useCallback(async () => {
    if (!programmeId) {
      setPortfolio(null);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setPortfolio(await api.getMyPortfolio(programmeId));
    } catch (err) {
      console.error("Failed to fetch portfolio:", err);
      setError(err instanceof Error ? err.message : "Failed to load portfolio");
    } finally {
      setIsLoading(false);
    }
  }, [programmeId]);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  // The skill vocabulary is fixed and identical for everyone, so it is fetched
  // once and kept out of the portfolio's own loading/error state: a picker that
  // fails to load must not make the rest of the settings look broken (KC-474).
  useEffect(() => {
    let cancelled = false;
    api
      .getPortfolioSkillOptions()
      .then((options) => {
        if (!cancelled) setSkillOptions(options);
      })
      .catch((err) => console.error("Failed to fetch portfolio skills:", err));
    return () => {
      cancelled = true;
    };
  }, []);

  const requireProgrammeId = () => {
    if (!programmeId) throw new Error("No programme selected");
    return programmeId;
  };

  const save = async (payload: MyPortfolioUpdatePayload) => {
    const updated = await api.updateMyPortfolio(requireProgrammeId(), payload);
    setPortfolio(updated);
    return updated;
  };

  const uploadPhoto = async (file: File) => {
    const updated = await api.uploadMyPortfolioPhoto(requireProgrammeId(), file);
    setPortfolio(updated);
    return updated;
  };

  const uploadCv = async (file: File) => {
    const updated = await api.uploadMyPortfolioCv(requireProgrammeId(), file);
    setPortfolio(updated);
    return updated;
  };

  const publish = async (consent: boolean) => {
    const updated = await api.publishMyPortfolio(requireProgrammeId(), consent);
    setPortfolio(updated);
    return updated;
  };

  const unpublish = async () => {
    const updated = await api.unpublishMyPortfolio(requireProgrammeId());
    setPortfolio(updated);
    return updated;
  };

  return {
    portfolio,
    skillOptions,
    isLoading,
    error,
    save,
    uploadPhoto,
    uploadCv,
    publish,
    unpublish,
  };
}
